import React from 'react';
import { fetchGlobal } from '../api/axios';
import './SearchBar.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from '../utilities/appToast';
import { setBusquedaGlobalResultados } from '../actions/busquedaGlobalActions';

const BUSQUEDA_GLOBAL_PATHS = ['/BusquedaGlobalResultados', '/busqueda-global-resultados'];
const BUSQUEDA_GLOBAL_MIN_LENGTH = 4;

const isBusquedaGlobalPath = (pathname = '') => {
    const normalizedPath = pathname.toLowerCase();
    return BUSQUEDA_GLOBAL_PATHS.some(path => path.toLowerCase() === normalizedPath);
};

const extractApiErrorMessages = (error) => {
    const data = error?.response?.data;
    if (!data) return [];

    if (Array.isArray(data?.errores)) return data.errores.filter(Boolean);

    if (data?.errors && typeof data.errors === 'object') {
        return Object.values(data.errors)
            .flat()
            .filter(Boolean);
    }

    if (typeof data?.title === 'string' && data.title.trim()) return [data.title];
    return [];
};


// Adaptación para usar useNavigate en class component
function SearchBarWrapper(props) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    return <SearchBar {...props} navigate={navigate} location={location} dispatch={dispatch} />;
}

class SearchBar extends React.Component {
    state = {
        value: '',
        loading: false,
        showTooltip: false
    };
    inputRef = React.createRef();

    handleInputChange = (e) => {
        this.setState({ value: e.target.value });
    };

    componentDidMount() {
        if (this.props.listenClear) {
            window.addEventListener('clearSearchInput', this.handleClearInput);
        }
    }

    componentDidUpdate(prevProps) {
        const previousPathname = prevProps.location?.pathname;
        const currentPathname = this.props.location?.pathname;

        if (
            previousPathname !== currentPathname &&
            isBusquedaGlobalPath(previousPathname) &&
            !isBusquedaGlobalPath(currentPathname)
        ) {
            this.clearInput({ focus: false });
        }
    }

    componentWillUnmount() {
        if (this.props.listenClear) {
            window.removeEventListener('clearSearchInput', this.handleClearInput);
        }
    }

    clearInput = ({ focus = true } = {}) => {
        this.setState({ value: '' }, () => {
            if (this.inputRef.current) {
                this.inputRef.current.value = '';
                if (focus) {
                    this.inputRef.current.focus();
                }
            }
        });
    }

    handleClearInput = () => {
        this.clearInput();
    }

    handleSearchInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            this.handleBuscar();
        }
    }

    handleBuscar = () => {
        const { value } = this.state;
        const { idSeller, navigate, dispatch } = this.props;
        const normalizedValue = value.trim();

        if (!normalizedValue) {
            this.setState({ showTooltip: true });
            setTimeout(() => {
                this.setState({ showTooltip: false });
            }, 2000);
            return;
        }

        if (normalizedValue.length < BUSQUEDA_GLOBAL_MIN_LENGTH) {
            toast.error(`La entrada debe tener al menos ${BUSQUEDA_GLOBAL_MIN_LENGTH} caracteres`);
            return;
        }

        this.setState({ loading: true });
        // Navega primero mostrando el spinner
        if (navigate) {
            navigate('/BusquedaGlobalResultados', { state: { loadingGlobal: true } });
        }
        fetchGlobal(normalizedValue, idSeller)
            .then(res => {
                if (dispatch) {
                    dispatch(setBusquedaGlobalResultados(res));
                }
                if (navigate) {
                    // Actualiza la página con los resultados
                    navigate('/BusquedaGlobalResultados', { state: { resultados: res, loadingGlobal: false } });
                }
                if (this.props.onSearch) {
                    this.props.onSearch(res);
                }
            })
            .catch(err => {
                console.error('Error búsqueda global:', err);
                const errors = extractApiErrorMessages(err);

                if (errors.length > 0) {
                    errors.forEach(error => toast.error(error));

                    if (dispatch) {
                        dispatch(setBusquedaGlobalResultados(null));
                    }

                    if (navigate) {
                        navigate('/BusquedaGlobalResultados', {
                            state: {
                                resultados: { publicaciones: [], ordenes: [] },
                                loadingGlobal: false
                            }
                        });
                    }
                    return;
                }

                const errorPayload = { tieneError: true, publicaciones: [], ordenes: [] };
                if (dispatch) {
                    dispatch(setBusquedaGlobalResultados(errorPayload));
                }
                if (navigate) {
                    navigate('/BusquedaGlobalResultados', {
                        state: { resultados: errorPayload, loadingGlobal: false }
                    });
                }
            })
            .finally(() => {
                this.setState({ loading: false });
            });
    }

    render() {
        return (
            <div className="d-flex align-items-center ms-2 position-relative" style={{ width: '100%' }}>
                <input
                    ref={this.inputRef}
                    type="text"
                    className="form-control searchbar-input"
                    placeholder="Ingrese orden, publicación o Sku "
                    onKeyDown={this.handleSearchInputKeyDown}
                    onChange={this.handleInputChange}
                    value={this.state.value}
                    disabled={this.state.loading}
                    required
                    minLength={BUSQUEDA_GLOBAL_MIN_LENGTH}
                    title="Debe ingresar algún tipo de dato"
                    onInvalid={e => e.target.setCustomValidity('Debe ingresar algún tipo de dato')}
                    onInput={e => e.target.setCustomValidity('')}
                    autoComplete="off"
                />
                <span className="searchbar-icon-wrapper" onClick={this.handleBuscar}>
                    <i className="fas fa-search searchbar-icon" />
                </span>
                {this.state.showTooltip && (
                    <div style={{ position: 'absolute', top: '110%', left: '0', zIndex: 10 }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            background: '#fff',
                            border: '1px solid #212529',
                            color: '#212529',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                            padding: '8px 16px',
                            fontSize: '1rem',
                            fontWeight: 400,
                            minWidth: '280px',
                            whiteSpace: 'nowrap'
                        }}>
                            <span style={{ marginRight: '12px', display: 'flex', alignItems: 'center' }}>
                                <i className="fas fa-exclamation-triangle" style={{ color: '#fd7e14', fontSize: '1.2rem' }}></i>
                            </span>
                            <span>Debe ingresar algún tipo de dato</span>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}



export default SearchBarWrapper;
